/**
 * Turning a stored product-media PATH into a URL a `<video>` can play.
 *
 * Uploads produce a path (`/notifications/media/<uuid>.mp4`), never a URL —
 * deliberately, so the stored value is structurally incapable of pointing
 * off-platform and no host allowlist is needed. The cost of that choice is that
 * every consumer has to rejoin the path to the API origin; this is the admin
 * app's half of that.
 *
 * The frontend has its own `resolveNotificationMediaUrl`
 * (legal-opinion-frontend `utils/notifications/notificationMedia.ts`). This is
 * NOT a fork of it — that one folds in the notifications feature flag and
 * carries two legacy image shapes, neither of which applies here, and the two
 * apps read different env vars (`VITE_*` here, `REACT_APP_*` there). Sharing
 * would mean exporting across repos that have no build-time link.
 */

import { isVideoMediaPath } from '../constants/notificationMediaTypes';

/**
 * Same resolution order as `services/api.ts` — one origin for the whole app, so
 * a media URL can never point at a different environment than the API calls do.
 */
const API_BASE_URL: string =
  import.meta.env.VITE_API_URL || 'https://api.intelligent-automation.net';

/**
 * A playable absolute URL, or `''` when the value is not an upload-produced
 * video path.
 *
 * Returning `''` rather than throwing is what lets callers treat "no video",
 * "malformed reference" and "someone pasted a URL" as one branch — an entry
 * then renders as text instead of showing a broken player, which is the
 * degrade-when-absent shape the rest of this surface already uses.
 */
export function resolveVideoMediaUrl(value?: string): string {
  if (!isVideoMediaPath(value)) return '';
  return `${API_BASE_URL}${value}`;
}
